<template>
	<div class="kpi-builder">
		<LoadingOverlay :visible="isPageLoading || isKpiDetailLoading" :message="t('Loading')" />

		<v-card class="filters-card" flat>
			<v-card-title>
				<md-icon>filter_list</md-icon>
				{{ t('Filters') }}

				<UserViewControl :view-key="'KpiBuilder'" :get-view-state="getUserViewState" :apply-view-state="applyUserViewState" :default-view-state="getDefaultUserViewState" :is-ready="isUserViewReady" :state-version="viewStateVersion" @view-applied="onUserViewApplied" />

				<div class="filter-chips" v-if="!isTablet">
					<v-chip v-for="chip in selectedFilterChips" :key="chip.key" color="primary" variant="tonal" closable @click:close.stop="removeFilterChip(chip.key)">
						{{ chip.label }}
					</v-chip>
				</div>
			</v-card-title>
			<div class="filters-container">
				<div class="filters filters-main">
					<v-switch v-model="allPark" :label="t('AllPark')" color="primary" hide-details density="compact" inset />
					<SelectControl ref="kpiReferenceSelect" :label="t('Kpis')" :items="kpis" :title-key="'referenceTranslated'" :required="true" :pre-selected="preSelectedKpi?.id" />
					<TreeViewControl ref="organisationTreeview" :label="t('Organisations')" :items="organisations" :title-key="'label'" :multiple="true" />
					<SelectControl ref="accountSelect" :label="t('Accounts')" :items="accounts" :multiple="true" v-if="!isTouchMode" />
					<SelectControl ref="channelSelect" :label="t('Channels')" :items="channels" :multiple="true" v-if="!isTouchMode" />

					<transition name="filters-fold">
						<div v-show="showAdditionalFilters" class="filters filters-additional">
							<SelectControl ref="accountSelect" :label="t('Accounts')" :items="accounts" :multiple="true" v-if="isTouchMode" />
							<SelectControl ref="channelSelect" :label="t('Channels')" :items="channels" :multiple="true" v-if="isTouchMode" />
							<SelectControl ref="deliveryLocationTypeSelect" :label="t('DeliveryLocationType')" :items="deliveryLocationTypes" :multiple="true" />
							<TreeViewControl ref="productTreeTreeview" :label="t('ProductTrees')" :items="productTrees" :title-key="'label'" :multiple="true" />
							<SelectControl ref="specificitySelect" :label="t('Specificities')" :items="specificities" :multiple="true" :title-key="'value'" />
							<SelectControl ref="companySelect" :label="t('Companies')" :items="companies" :multiple="true" :title-key="'companyName'" />
						</div>
					</transition>
				</div>

				<div class="filters-actions">
					<v-btn @click="toggleAdditionalFilters" color="secondary" variant="tonal" rounded="xl">
						<md-icon>{{ showAdditionalFilters ? 'keyboard_arrow_up' : 'keyboard_arrow_down' }}</md-icon>
						{{ showAdditionalFilters ? t('Less') : t('More') }}
					</v-btn>
					<v-btn @click="resetFilters" color="danger" variant="tonal" rounded="xl">
						<md-icon>filter_alt_off</md-icon>
						{{ t('Clear') }}
					</v-btn>
					<v-btn @click="applyFilters" color="primary" variant="tonal" rounded="xl">
						<md-icon>filter_alt</md-icon>
						{{ t('Apply') }}
					</v-btn>
				</div>
			</div>
		</v-card>

		<v-expansion-panels v-model="expandedKpiPanel">
			<v-expansion-panel :value="KPI_LIST_PANEL_KEY" class="kpi-panel" eager>
				<v-expansion-panel-title>
					<md-icon>swap_driving_apps_wheel</md-icon>
					{{ t('Kpis') }}
					<v-badge color="primary" :content="kpiComponents.length + 1" inline></v-badge>
				</v-expansion-panel-title>
				<v-expansion-panel-text>
					<transition-group name="pop" tag="div" class="kpi-list">
						<div class="main-kpi" key="main-kpi">
							<Kpi :title="mainKpiTitle" :color="props.color" :width="props.width" :params="mainKpiParams" :annotation="t('KpiGlobal')" :need-title="false" @kpi-apply-filters="reapplyKpiFilters" />
						</div>
						<div class="custom-kpi" v-for="(customKpi, index) in kpiComponents" :key="customKpi.guid || index">
							<Kpi :title="customKpi.title" :color="customKpi.color" :width="customKpi.width" :params="customKpi.params" :annotation="customKpi.annotation" :custom="true" :guid="customKpi.guid" :need-title="false" @kpi-apply-filters="reapplyKpiFilters" />
						</div>
						<div v-if="hasAppliedFilters" class="add-custom-kpi" key="add-custom-kpi">
							<v-tooltip :attach="overlayTarget" location="bottom">
								<template v-slot:activator="{ props }">
									<v-btn v-bind="props" icon="add_2" @click="createCustomKpiManually" variant="tonal" color="primary" size="x-large" class="pulse"></v-btn>
								</template>
								<span>{{ t('AddNewKpi') }}</span>
							</v-tooltip>
						</div>
					</transition-group>
				</v-expansion-panel-text>
			</v-expansion-panel>
		</v-expansion-panels>

		<v-expansion-panels v-if="clusterKpiItems.length > 0">
			<v-expansion-panel class="kpi-panel" eager>
				<v-expansion-panel-title>
					<md-icon>hub</md-icon>
					{{ t('KpiByCluster') }}
					<v-badge color="primary" :content="clusterKpiItems.length" inline></v-badge>
				</v-expansion-panel-title>
				<v-expansion-panel-text>
					<transition-group name="pop" tag="div" class="kpi-list">
						<div class="custom-kpi" v-for="clusterKpi in clusterKpiItems" :key="clusterKpi.key">
							<Kpi :title="clusterKpi.title" :color="props.color" :width="props.width" :params="clusterKpi.params" :annotation="clusterKpi.annotation" :need-title="false" @kpi-apply-filters="reapplyKpiFilters" />
						</div>
					</transition-group>
				</v-expansion-panel-text>
			</v-expansion-panel>
		</v-expansion-panels>

		<v-card ref="kpiDetailCard" class="kpi-detail-card" flat>
			<v-card-title class="kpi-detail-card__title">
				<div class="kpi-detail-card__heading">
					<div class="kpi-detail-card__title-text">
						<md-icon>insights</md-icon>
						{{ detailTableTitle }}
					</div>

					<div class="d-flex ga-2 ml-2">
						<v-chip readonly>
							<md-icon>storefront</md-icon>
							{{ displayedDeliveryLocationCount }} {{ t('DeliveryLocations') }}
						</v-chip>
						<v-chip :color="isDetailSimulated ? 'primary' : 'default'" variant="tonal" readonly>
							<md-icon>{{ isDetailSimulated ? 'play_circle' : 'radio_button_checked' }}</md-icon>
							{{ isDetailSimulated ? t('SimulatedData') : t('RealData') }}
						</v-chip>
					</div>
					<QuickRowGroup v-model="selectedGridRowGroups" :groups="quickRowGroupDefinitions" :default-active-groups="defaultActiveGridRowGroups" />
				</div>
				<div class="kpi-detail-card__toolbar">
					<v-text-field v-model="kpiDetailSearch" :label="t('Search')" density="compact" variant="outlined" prepend-inner-icon="search" clearable hide-details />
					<v-tooltip :attach="overlayTarget" location="bottom">
						<template v-slot:activator="{ props: tooltipProps }">
							<v-btn v-bind="tooltipProps" :icon="isFullscreen ? 'fullscreen_exit' : 'fullscreen'" variant="tonal" density="comfortable" @click="toggleGridFullscreen" />
						</template>
						<span>{{ isFullscreen ? t('ExitFullscreen') : t('Fullscreen') }}</span>
					</v-tooltip>
				</div>
			</v-card-title>
			<v-card-text>
				<v-alert v-if="kpiDetailError" type="error" variant="tonal" density="comfortable" class="mb-4">
					{{ kpiDetailError }}
				</v-alert>

				<div class="kpi-detail-card__grid-shell">
					<div v-show="showKpiDetailGrid" class="kpi-detail-card__grid">
						<AgGrid
							ref="kpiDetailGrid"
							:rowData="filteredKpiDetailRows"
							:getRowId="getKpiDetailRowId"
							:colDefs="kpiDetailColumns"
							:getGroupRowAgg="getKpiDetailGroupRowAgg"
							:sideBar="isTouchMode ? false : true"
							:row-group-panel-show="isTouchMode ? 'false' : 'always'"
							:pagination="true"
							:paginationPageSize="50"
							@displayed-row-count-changed="onDisplayedRowCountChanged"
							@row-group-columns-changed="onGridRowGroupColumnsChanged"
							@grid-ready="onKpiDetailGridReady"
							@view-state-changed="onDetailGridViewStateChanged"
						/>
					</div>

					<v-sheet v-if="!showKpiDetailGrid || (!isKpiDetailLoading && !kpiDetailError && filteredKpiDetailRows.length === 0)" class="kpi-detail-card__empty kpi-detail-card__empty--overlay" rounded>
						<template v-if="!showKpiDetailGrid && hasAppliedFilters">
							<v-btn @click="showDetailGrid" color="primary" variant="tonal" rounded="xl" size="large" class="mt-4">
								<md-icon>visibility</md-icon>
								{{ t('ShowDetailGrid') }}
							</v-btn>
						</template>
						<template v-else>
							<md-icon>table_rows_narrow</md-icon>
							<span>{{ detailEmptyStateMessage }}</span>
						</template>
					</v-sheet>
				</div>
			</v-card-text>
		</v-card>
	</div>
</template>

<script setup>
import { ref, computed, onMounted, onBeforeUnmount, inject, watch, nextTick } from 'vue';
import { useTranslator } from '@/utils/useTranslator';
import { iframeDataBridge } from '@/iframe/iframeDataBridge';
import { kpiService } from '@/services/kpiService';
import { eventBus } from '@/utils/eventBus';
import { deviceUtils } from '@/utils/deviceUtils';

import Kpi from '@/components/kpi/Kpi.vue';
import AgGrid from '@/components/agGrid/AgGrid.vue';
import ProgressBarRenderer from '@/components/agGrid/ProgressBarRenderer.vue';
import CompanyNameRenderer from '@/components/agGrid/CompanyNameRenderer.vue';
import SelectControl from '@/components/controls/SelectControl.vue';
import TreeViewControl from '@/components/controls/TreeViewControl.vue';
import UserViewControl from '@/components/controls/UserViewControl.vue';

import { getOrganisationsTree, getOrganisationMaxLevel } from '@/api/core/organisation';
import { getAccounts } from '@/api/distributor/account';
import { getChannels } from '@/api/distributor/channel';
import { getProductTrees, getProductTreesTree } from '@/api/core/productTree';
import { getAttributeById, getAttributes } from '@/api/core/attribute';
import { getDeliveryLocationAttributes } from '@/api/sales/deliveryLocationAttribute';
import { getCompanies } from '@/api/core/company';
import { getUserSettings, postUserSettings } from '@/api/core/userSetting';
import QuickRowGroup from '@/components/agGrid/QuickRowGroup.vue';
import LoadingOverlay from '@/components/common/LoadingOverlay.vue';

const t = useTranslator();
const ecosVM = iframeDataBridge.getEcosVM();
const user = iframeDataBridge.getCurrentUser();
const toast = iframeDataBridge.getIframeWindow().toast;
const overlayTarget = inject('overlayTarget', null);
const KPI_BUILDER_CUSTOMS_SETTING_NAME = 'VueCustomKpis';
const isTouchMode = ref(deviceUtils.isTouchMode());
const isTablet = ref(deviceUtils.isTablet());
const onResize = () => {
	isTouchMode.value = deviceUtils.isTouchMode();
	isTablet.value = deviceUtils.isTablet();
};

const props = defineProps({
	title: {
		type: String,
		required: true,
	},
	color: {
		type: String,
		default: 'primary',
	},
	width: {
		type: Number,
		default: 12,
	},
	params: {
		type: Object,
		default: () => ({}),
	},
	guid: {
		type: String,
		default: null,
	},
});

const error = ref(null);
const sortedWidgets = inject('sortedWidgets', ref([]));
const activeKpiReference = ref(props.params.reference || '');
const userSettings = ref([]);

const kpis = ref([]);
const organisations = ref([]);
const deliveryLocationTypes = ref([]);
const accounts = ref([]);
const channels = ref([]);
const productTrees = ref([]);
const productTreesWithDepth = ref([]);
const specificities = ref([]);
const companies = ref([]);

const kpiReferenceSelect = ref(null);
const organisationTreeview = ref(null);
const deliveryLocationTypeSelect = ref(null);
const accountSelect = ref(null);
const channelSelect = ref(null);
const productTreeTreeview = ref(null);
const specificitySelect = ref(null);
const companySelect = ref(null);
const showAdditionalFilters = ref(false);
const allPark = ref(true);
const showKpiDetailGrid = ref(false);
const selectedFilterChips = ref([]);

const kpiDetailRows = ref([]);
const kpiDetailSearch = ref('');
const isKpiDetailLoading = ref(false);
const isPageLoading = ref(true);
const kpiDetailError = ref(null);
const deliveryLocationAttributeColumns = ref([]);
const attributesById = ref(new Map());
const kpiDetailGrid = ref(null);
const kpiDetailCard = ref(null);
const isFullscreen = ref(false);
const selectedGridRowGroups = ref([]);
const displayedDeliveryLocationCount = ref(0);
const isDetailSimulated = ref(Boolean(props.params?.simulated));
const currentAppliedFilters = ref(null);
const viewStateVersion = ref(0);
const isKpiDetailGridReady = ref(false);
const isUserViewReady = ref(false);
const detailGridReadyResolvers = [];
const USER_VIEW_DEFAULT_ID = '__default__';

const GRID_GROUP_COLUMNS = {
	DELIVERY_LOCATION: 'rowGroupDeliveryLocation',
	ACCOUNT: 'rowGroupAccount',
	CHANNEL: 'rowGroupChannel',
	DELIVERY_LOCATION_TYPE: 'rowGroupDeliveryLocationType',
	LOCATION_GROUP: 'rowGroupLocationGroup',
	CLUSTER: 'rowGroupCluster',
	SPECIFICITIES: 'rowGroupSpecificities',
};
const DEFAULT_DETAIL_GRID_ROW_GROUPS = [GRID_GROUP_COLUMNS.DELIVERY_LOCATION];
const DEFAULT_DETAIL_GRID_VISIBLE_COLUMNS = ['value', 'difference', 'referenceCount', 'totalReferencesCount'];
selectedGridRowGroups.value = [...DEFAULT_DETAIL_GRID_ROW_GROUPS];
const defaultActiveGridRowGroups = ref([...DEFAULT_DETAIL_GRID_ROW_GROUPS]);
let kpiDetailLoadRequestId = 0;
let latestKpiDetailLoadKey = '';
let isApplyingViewState = false;
const deliveryLocationAttributeCache = new Map();

const filterRefs = {
	kpiReferenceSelect,
	organisationTreeview,
	deliveryLocationTypeSelect,
	accountSelect,
	channelSelect,
	productTreeTreeview,
	specificitySelect,
	companySelect,
};

const KPI_LIST_PANEL_KEY = 'kpi-list';
const localKpiComponents = ref([]);
const expandedKpiPanel = ref(KPI_LIST_PANEL_KEY);
const hasAppliedFilters = ref(false);

const preSelectedKpi = computed(() => findKpiByReference(activeKpiReference.value));
const matchingWidgets = computed(() => {
	if (!Array.isArray(sortedWidgets.value)) {
		return [];
	}

	return sortedWidgets.value.filter((widget) => hasSameReference(widget?.params?.reference, activeKpiReference.value));
});
const mainWidget = computed(() => matchingWidgets.value.find((widget) => !widget.guid) || matchingWidgets.value.find((widget) => widget.id === props.guid) || matchingWidgets.value[0] || null);
const mainKpiTitle = computed(() => mainWidget.value?.title || getKpiTitle(activeKpiReference.value) || props.title);
const mainKpiParams = computed(() => {
	const params = mainWidget.value?.params || props.params || {};
	const baseParams = {
		reference: activeKpiReference.value || params.reference,
		type: params.type || 'kpi',
		shape: params.shape || 'circle',
		allPark: allPark.value,
	};

	if (params.icon) {
		baseParams.icon = params.icon;
	}

	if ('simulated' in params) {
		baseParams.simulated = params.simulated;
	}

	return baseParams;
});
const persistedKpiComponents = computed(() => matchingWidgets.value.filter((widget) => (widget.custom || !!widget.guid) && widget.guid !== props.guid && widget.id !== props.guid).map((widget) => mapWidgetToCustomKpi(widget)));
const kpiComponents = computed(() => {
	if (!hasAppliedFilters.value) {
		return [];
	}

	const mergedComponents = new Map();

	persistedKpiComponents.value.forEach((customKpi) => {
		mergedComponents.set(customKpi.guid, customKpi);
	});

	localKpiComponents.value
		.filter((customKpi) => hasSameReference(customKpi.params?.reference, activeKpiReference.value))
		.forEach((customKpi) => {
			mergedComponents.set(customKpi.guid, customKpi);
		});

	return Array.from(mergedComponents.values());
});
const detailTableTitle = computed(() => mainKpiTitle.value?.trim() || t('Kpis'));
const clusterKpiItems = computed(() => {
	if (!hasAppliedFilters.value || !currentAppliedFilters.value?.reference) {
		return [];
	}

	const clustersById = new Map();
	(Array.isArray(kpiDetailRows.value) ? kpiDetailRows.value : []).forEach((row) => {
		const rawClusterId = Number(row?.clusterId);
		if (!Number.isInteger(rawClusterId) || rawClusterId <= 0) {
			return;
		}

		if (!clustersById.has(rawClusterId)) {
			clustersById.set(rawClusterId, row?.cluster || `${t('Cluster')} ${rawClusterId}`);
		}
	});

	return Array.from(clustersById.entries())
		.map(([clusterId, clusterName]) => ({
			key: `cluster-${clusterId}`,
			title: mainKpiTitle.value,
			annotation: clusterName,
			params: {
				...currentAppliedFilters.value,
				clusterId,
			},
		}))
		.sort((firstItem, secondItem) => firstItem.annotation.localeCompare(secondItem.annotation));
});
const detailEmptyStateMessage = computed(() => {
	if (!activeKpiReference.value) {
		return t('PleaseSelectKpi');
	}

	return t('NoData');
});
const isDetailPercentage = computed(() => kpiService.isPercentage(activeKpiReference.value || props.params.reference || ''));
const productTreeMaxLevel = computed(() => {
	const maxDepth = Math.max(...(Array.isArray(productTreesWithDepth.value) ? productTreesWithDepth.value : []).map((productTree) => Number(productTree?.depth || 0)), 0);
	return Number.isFinite(maxDepth) && maxDepth >= 0 ? Math.trunc(maxDepth) + 1 : 0;
});
const organisationRowGroupColumns = computed(() => {
	return Array.from({ length: organisationMaxLevel.value || 0 }, (_, index) => {
		const level = index + 1;
		return {
			colId: `rowGroupOrganisationLevel${level}`,
			field: `organisationLevel${level}`,
			headerName: `${t('Organisation')} ${t('Level')} ${level}`,
			hide: true,
			enableRowGroup: true,
			suppressColumnsToolPanel: true,
		};
	});
});
const categoryRowGroupColumns = computed(() => {
	return Array.from({ length: productTreeMaxLevel.value || 0 }, (_, index) => {
		const level = index + 1;
		return {
			colId: `rowGroupProductTreeLevel${level}`,
			field: `productTreeLevel${level}`,
			headerName: `${t('Level')} ${level}`,
			hide: true,
			enableRowGroup: true,
			suppressColumnsToolPanel: true,
		};
	});
});
const quickRowGroupDefinitions = computed(() => {
	const groups = [
		{ key: 'deliveryLocation', label: t('DeliveryLocation'), columnField: GRID_GROUP_COLUMNS.DELIVERY_LOCATION },
		{ key: 'account', label: t('Account'), columnField: GRID_GROUP_COLUMNS.ACCOUNT },
		{ key: 'channel', label: t('Channel'), columnField: GRID_GROUP_COLUMNS.CHANNEL },
		{ key: 'locationGroup', label: t('Group'), columnField: GRID_GROUP_COLUMNS.LOCATION_GROUP },
		{ key: 'cluster', label: t('Cluster'), columnField: GRID_GROUP_COLUMNS.CLUSTER },
		{ key: 'specificities', label: t('Specificities'), columnField: GRID_GROUP_COLUMNS.SPECIFICITIES },
	];

	if (productTreeMaxLevel.value > 0) {
		groups.push({
			key: 'categories',
			label: t('Categories'),
			levels: Array.from({ length: productTreeMaxLevel.value }, (_, index) => ({
				key: `producttree-level-${index + 1}`,
				label: `${t('Level')} ${index + 1}`,
				columnField: `rowGroupProductTreeLevel${index + 1}`,
			})),
		});
	}

	if (organisationMaxLevel.value > 0) {
		groups.push({
			key: 'organisation',
			label: t('Organisation'),
			levels: Array.from({ length: organisationMaxLevel.value }, (_, index) => ({
				key: `organisation-level-${index + 1}`,
				label: `${t('Level')} ${index + 1}`,
				columnField: `rowGroupOrganisationLevel${index + 1}`,
			})),
		});
	}

	return groups;
});
const kpiDetailColumns = computed(() => [
	{
		colId: GRID_GROUP_COLUMNS.DELIVERY_LOCATION,
		field: 'deliveryLocation',
		headerName: t('DeliveryLocation'),
		hide: true,
		enableRowGroup: true,
		rowGroup: true,
		rowGroupIndex: 0,
		suppressColumnsToolPanel: true,
		valueGetter: ({ data }) => data?.deliveryLocation || data?.companyName || '',
	},
	{
		colId: GRID_GROUP_COLUMNS.ACCOUNT,
		field: 'account',
		headerName: t('Account'),
		hide: true,
		enableRowGroup: true,
		suppressColumnsToolPanel: true,
	},
	{
		colId: GRID_GROUP_COLUMNS.CHANNEL,
		field: 'channel',
		headerName: t('Channel'),
		hide: true,
		enableRowGroup: true,
		suppressColumnsToolPanel: true,
	},
	{
		colId: GRID_GROUP_COLUMNS.LOCATION_GROUP,
		field: 'locationGroup',
		headerName: t('Group'),
		hide: true,
		enableRowGroup: true,
		suppressColumnsToolPanel: true,
	},
	{
		colId: GRID_GROUP_COLUMNS.CLUSTER,
		field: 'cluster',
		headerName: t('Cluster'),
		hide: true,
		enableRowGroup: true,
		suppressColumnsToolPanel: true,
	},
	{
		colId: GRID_GROUP_COLUMNS.SPECIFICITIES,
		field: 'specificities',
		headerName: t('Specificities'),
		hide: true,
		enableRowGroup: true,
		suppressColumnsToolPanel: true,
	},
	...categoryRowGroupColumns.value,
	...organisationRowGroupColumns.value,
	{
		headerName: t('DeliveryLocation'),
		groupId: 'deliveryLocation',
		openByDefault: false,
		marryChildren: true,
		children: [
			{
				field: 'companyName',
				headerName: t('CompanyName'),
				sortable: true,
				filter: true,
				hide: isDefaultDetailGridColumnHidden('companyName'),
				resizable: true,
				minWidth: isTouchMode.value ? 150 : 220,
				cellRenderer: CompanyNameRenderer,
			},
			{
				field: 'parentAccount',
				headerName: t('ParentAccount'),
				sortable: true,
				filter: true,
				hide: true,
				resizable: true,
				minWidth: 160,
			},
			{
				field: 'account',
				headerName: t('Account'),
				sortable: true,
				filter: true,
				hide: isDefaultDetailGridColumnHidden('account'),
				resizable: true,
				minWidth: 160,
			},
			{
				field: 'channel',
				headerName: t('Channel'),
				sortable: true,
				filter: true,
				hide: isDefaultDetailGridColumnHidden('channel'),
				resizable: true,
				minWidth: 140,
			},
			{
				field: 'deliveryLocationType',
				headerName: t('DeliveryLocationType'),
				sortable: true,
				filter: true,
				hide: isDefaultDetailGridColumnHidden('deliveryLocationType'),
				resizable: true,
				minWidth: 160,
				columnGroupShow: 'open',
			},
			{
				field: 'address',
				headerName: t('Address'),
				sortable: true,
				filter: true,
				hide: isDefaultDetailGridColumnHidden('address'),
				resizable: true,
				minWidth: 220,
				columnGroupShow: 'open',
			},
			{
				field: 'postcode',
				headerName: t('Post Code'),
				sortable: true,
				filter: true,
				hide: isDefaultDetailGridColumnHidden('postcode'),
				resizable: true,
				minWidth: 120,
				columnGroupShow: 'open',
			},
			{
				field: 'city',
				headerName: t('City'),
				sortable: true,
				filter: true,
				hide: isDefaultDetailGridColumnHidden('city'),
				resizable: true,
				minWidth: 140,
				columnGroupShow: 'open',
			},
			{
				field: 'posType',
				headerName: t('PosType'),
				sortable: true,
				filter: true,
				hide: isDefaultDetailGridColumnHidden('posType'),
				resizable: true,
				minWidth: 140,
				columnGroupShow: 'open',
			},
			{
				field: 'cluster',
				headerName: t('Cluster'),
				sortable: true,
				filter: true,
				hide: isDefaultDetailGridColumnHidden('cluster'),
				resizable: true,
				minWidth: 140,
				columnGroupShow: 'open',
			},
			{
				field: 'locationGroup',
				headerName: t('Group'),
				sortable: true,
				filter: true,
				hide: isDefaultDetailGridColumnHidden('locationGroup'),
				resizable: true,
				minWidth: 140,
				columnGroupShow: 'open',
			},
			{
				field: 'distributionChannel',
				headerName: t('DistributionChannel'),
				sortable: true,
				filter: true,
				hide: isDefaultDetailGridColumnHidden('distributionChannel'),
				resizable: true,
				minWidth: 160,
				columnGroupShow: 'open',
			},
			{
				field: 'potential',
				headerName: t('Potential'),
				sortable: true,
				filter: true,
				hide: isDefaultDetailGridColumnHidden('potential'),
				resizable: true,
				minWidth: 120,
				cellDataType: 'number',
				columnGroupShow: 'open',
			},
			{
				field: 'seasonality',
				headerName: t('DeliveryLocationSeasonality'),
				sortable: true,
				filter: true,
				hide: isDefaultDetailGridColumnHidden('seasonality'),
				resizable: true,
				minWidth: 160,
				columnGroupShow: 'open',
			},
			...deliveryLocationAttributeColumns.value,
		],
	},
	{
		field: 'value',
		headerName: detailTableTitle.value,
		sortable: true,
		filter: true,
		resizable: true,
		minWidth: isTouchMode.value ? 150 : 240,
		cellDataType: 'number',
		cellRenderer: ProgressBarRenderer,
		cellRendererParams: () => ({
			formatMetric: formatKpiMetric,
			objectiveLabel: t('Objective'),
			needLabel: false,
			isPercentage: isDetailPercentage.value,
			bufferColor: 'grey-lighten-1',
			bufferOpacity: 0.35,
		}),
		valueFormatter: ({ value }) => formatKpiMetric(value),
		sort: 'desc',
	},
	{
		field: 'objective',
		headerName: t('Objective'),
		sortable: true,
		filter: true,
		hide: isDefaultDetailGridColumnHidden('objective'),
		resizable: true,
		maxWidth: 200,
		cellDataType: 'number',
		enableValue: true,
		valueFormatter: ({ value }) => formatKpiMetric(value),
	},
	{
		field: 'difference',
		headerName: t('Difference'),
		sortable: true,
		filter: true,
		hide: isDefaultDetailGridColumnHidden('difference'),
		resizable: true,
		maxWidth: 200,
		cellDataType: 'number',
		enableValue: true,
		valueFormatter: ({ value }) => formatKpiMetric(value, true),
		cellStyle: ({ value }) => getDifferenceCellStyle(value),
	},
	{
		headerName: t('Categories'),
		groupId: 'categories',
		openByDefault: false,
		marryChildren: true,
		hide: true,
		children: [
			...Array.from({ length: productTreeMaxLevel.value || 0 }, (_, index) => ({
				field: `productTreeLevel${index + 1}`,
				headerName: `${t('Level')} ${index + 1}`,
				sortable: true,
				filter: true,
				resizable: true,
				hide: isDefaultDetailGridColumnHidden(`productTreeLevel${index + 1}`),
				minWidth: 180,
				columnGroupShow: index === 0 ? null : 'open',
			})),
		],
	},
	{
		field: 'specificities',
		headerName: t('Specificities'),
		sortable: true,
		filter: true,
		hide: isDefaultDetailGridColumnHidden('specificities'),
		resizable: true,
		minWidth: 220,
	},
	{
		field: 'organisations',
		headerName: t('Organisation'),
		sortable: true,
		filter: true,
		hide: true,
		resizable: true,
		width: 180,
		children: Array.from({ length: organisationMaxLevel.value || 0 }, (_, index) => ({
			field: `organisationLevel${index + 1}`,
			headerName: `${t('Level')} ${index + 1}`,
			sortable: true,
			filter: true,
			resizable: true,
			hide: isDefaultDetailGridColumnHidden(`organisationLevel${index + 1}`),
			width: 180,
			columnGroupShow: index === 0 ? null : 'open',
		})),
	},
	{
		headerName: t('CodesCount'),
		groupId: 'codesCount',
		openByDefault: false,
		marryChildren: true,
		hide: false,
		children: [
			{
				field: 'referenceCount',
				headerName: t('ReferencesCount'),
				sortable: true,
				filter: true,
				hide: isDefaultDetailGridColumnHidden('referenceCount'),
				resizable: true,
				maxWidth: 200,
				cellDataType: 'number',
				valueFormatter: ({ value }) => formatReferenceCount(value),
			},
			{
				field: 'totalReferencesCount',
				headerName: t('TotalReferencesCount'),
				sortable: true,
				filter: true,
				hide: isDefaultDetailGridColumnHidden('totalReferencesCount'),
				resizable: true,
				maxWidth: 220,
				cellDataType: 'number',
				valueFormatter: ({ value }) => formatReferenceCount(value),
			},
		],
	},
]);
const filteredKpiDetailRows = computed(() => {
	let rows = kpiDetailRows.value;

	if (isOrgGroupingActive.value) {
		rows = rows.flatMap((row) => {
			if (!Array.isArray(row._organisationPaths)) {
				return [row];
			}
			return row._organisationPaths.map((orgLevels, index) => ({
				...row,
				...orgLevels,
				id: index === 0 ? row.id : `${row.id}__org${index}`,
			}));
		});
	}

	const searchValue = kpiDetailSearch.value?.trim().toLowerCase();
	if (!searchValue) {
		return rows;
	}

	return rows.filter((row) => {
		const categoryValues = Array.from({ length: productTreeMaxLevel.value || 0 }, (_, index) => row[`productTreeLevel${index + 1}`]);
		const dynamicAttributeValues = deliveryLocationAttributeColumns.value.map((column) => row[column.field]);
		const searchableValues = [row.companyName, row.deliveryLocation, row.parentAccount, row.account, row.channel, row.deliveryLocationType, row.address, row.postcode, row.city, row.posType, row.cluster, row.locationGroup, row.distributionChannel, row.seasonality, row.organisations, row.reference, row.productTree, row.specificities, ...categoryValues, ...dynamicAttributeValues];
		return searchableValues.some((value) =>
			String(value || '')
				.toLowerCase()
				.includes(searchValue)
		);
	});
});

const organisationMaxLevel = ref(null);
const isOrgGroupingActive = computed(() => selectedGridRowGroups.value.some((colId) => colId.startsWith('rowGroupOrganisationLevel')));

const toggleGridFullscreen = () => {
	const el = kpiDetailCard.value?.$el ?? kpiDetailCard.value;
	if (!el) return;
	isFullscreen.value = !isFullscreen.value;
	el.classList.toggle('kpi-detail-card--fullscreen', isFullscreen.value);
};

onMounted(() => {
	eventBus.on('custom-kpi-removed', onCustomKpiRemoved);
	eventBus.on('kpi-simulated-changed', onKpiSimulatedChanged);
	init().finally(() => {
		isPageLoading.value = false;
	});

	window.addEventListener('resize', onResize);
});

onBeforeUnmount(() => {
	eventBus.off('custom-kpi-removed', onCustomKpiRemoved);
	eventBus.off('kpi-simulated-changed', onKpiSimulatedChanged);

	window.removeEventListener('resize', onResize);
});

watch(error, (newVal) => {
	if (newVal) {
		toast(newVal, { style: { background: 'red' } });
	}
});

watch(
	() => getKpiDetailLoadKey(buildFiltersFromParams(props.params)),
	async () => {
		const newParams = props.params || {};
		activeKpiReference.value = newParams?.reference || '';
		isDetailSimulated.value = Boolean(newParams?.simulated);

		if ('allPark' in newParams) {
			allPark.value = Boolean(newParams.allPark);
		}

		if (!kpis.value.length) {
			return;
		}

		await syncFiltersFromParams(newParams);
		if (showKpiDetailGrid.value) {
			const filters = buildFiltersFromParams(newParams);
			if (filters) filters.allPark = allPark.value;
			await loadKpiDetails(filters);
		}
	}
);

watch(
	selectedGridRowGroups,
	() => {
		applyDetailRowGrouping();
		bumpViewStateVersion();
	},
	{ deep: false }
);

watch(
	quickRowGroupDefinitions,
	(groups) => {
		const availableColumns = new Set(groups.flatMap((group) => (Array.isArray(group.levels) ? group.levels.map((level) => level.columnField) : [group.columnField])).filter(Boolean));
		const nextSelectedGroups = selectedGridRowGroups.value.filter((columnField) => availableColumns.has(columnField));

		if (nextSelectedGroups.length !== selectedGridRowGroups.value.length) {
			selectedGridRowGroups.value = nextSelectedGroups;
		}
	},
	{ deep: false, immediate: true }
);

watch(
	() => kpiDetailColumns.value,
	async () => {
		await nextTick();
		applyDetailRowGrouping();
	},
	{ deep: false }
);

watch(allPark, async (newVal, oldVal) => {
	if (isApplyingViewState || newVal === oldVal || !hasAppliedFilters.value) return;
	await applyFilters({ skipPersistGeneratedCustomKpi: true });
});

async function init() {
	isUserViewReady.value = false;
	await getFiltersData();
	await getRowGroupData();
	await loadPersistedCustomKpis();
	await syncFiltersFromParams(props.params);
	await nextTick();
	await waitForDetailGridReady();
	isUserViewReady.value = true;
}

function toggleAdditionalFilters() {
	showAdditionalFilters.value = !showAdditionalFilters.value;
}

async function getFiltersData() {
	kpis.value = kpiService.getAvailableKpis();
	organisations.value = await getOrganisationsTree();
	deliveryLocationTypes.value = typeof ecosVM?.deliveryLocationTypes === 'function' ? ecosVM.deliveryLocationTypes() : ecosVM?.deliveryLocationTypes || [];
	accounts.value = await getAccounts();
	channels.value = await getChannels();
	const [productTreeItems, productTreeTreeItems] = await Promise.all([getProductTrees(), getProductTreesTree()]);
	productTreesWithDepth.value = Array.isArray(productTreeItems) ? productTreeItems : [];
	productTrees.value = Array.isArray(productTreeTreeItems) ? productTreeTreeItems : [];
	const attributes = await getAttributes(false, 'tokenSales');
	attributesById.value = new Map((Array.isArray(attributes) ? attributes : []).map((attribute) => [Number(attribute.id), attribute]));
	const specificityAttribute = await getAttributeById(69, 'tokenAssort');
	specificities.value = specificityAttribute ? specificityAttribute.values : [];
	companies.value = await getCompanies();
}

async function getRowGroupData() {
	organisationMaxLevel.value = await getOrganisationMaxLevel();
}

async function loadPersistedCustomKpis() {
	userSettings.value = await getUserSettings({ groupName: 'WEBAPP', userId: user.id });
	const customKpiSettings = userSettings.value.find((setting) => setting.name === KPI_BUILDER_CUSTOMS_SETTING_NAME);
	localKpiComponents.value = parsePersistedCustomKpis(customKpiSettings?.value);
}

function parsePersistedCustomKpis(settingValue) {
	if (!settingValue) {
		return [];
	}

	try {
		const parsedValue = JSON.parse(settingValue);
		if (!Array.isArray(parsedValue)) {
			return [];
		}

		return parsedValue.map((customKpi) => mapStoredCustomKpi(customKpi)).filter(Boolean);
	} catch {
		error.value = 'Impossible de charger les KPIs personnalises enregistres.';
		return [];
	}
}

function buildFiltersFromParams(params = {}) {
	const reference = params.reference || activeKpiReference.value;
	if (!reference) {
		return null;
	}

	const filters = {
		reference,
		accounts: params.accounts || [],
		channels: params.channels || [],
		clusterId: params.clusterId ?? null,
		dlts: params.dlts || params.deliveryLocationTypes || [],
		productTrees: params.productTrees || params.pts || [],
		specificities: params.specificities || params.spes || [],
		organisations: params.organisations || [],
		companies: params.companies || [],
	};

	const simulatedValue = getSimulatedFilterValue(reference, params);
	if (simulatedValue !== undefined) {
		filters.simulated = simulatedValue;
	}

	filters.allPark = params.allPark ?? false;

	return filters;
}

function getSimulatedFilterValue(reference, params = {}) {
	if ('simulated' in params) {
		return Boolean(params.simulated);
	}

	if (reference && hasSameReference(reference, activeKpiReference.value) && 'simulated' in mainKpiParams.value) {
		return Boolean(isDetailSimulated.value);
	}

	if (reference && hasSameReference(reference, props.params?.reference) && 'simulated' in (props.params || {})) {
		return Boolean(props.params.simulated);
	}

	if (reference && hasSameReference(reference, mainKpiParams.value?.reference) && 'simulated' in mainKpiParams.value) {
		return Boolean(mainKpiParams.value.simulated);
	}

	return undefined;
}

async function syncFiltersFromParams(params = {}) {
	await nextTick();

	const selectedKpi = findKpiByReference(params.reference || activeKpiReference.value);
	if (selectedKpi && kpiReferenceSelect.value?.selectItems) {
		kpiReferenceSelect.value.selectItems(selectedKpi.id);
	}

	organisationTreeview.value?.selectItems(params.organisations || []);
	deliveryLocationTypeSelect.value?.selectItems(params.dlts || params.deliveryLocationTypes || []);
	accountSelect.value?.selectItems(params.accounts || []);
	channelSelect.value?.selectItems(params.channels || []);
	productTreeTreeview.value?.selectItems(params.productTrees || params.pts || []);
	specificitySelect.value?.selectItems(params.specificities || params.spes || []);
	companySelect.value?.selectItems(params.companies || []);

	updateSelectedFilterChips();
}

function buildFiltersFromControls(selectedKpi = null) {
	const kpiValue = selectedKpi || kpiReferenceSelect.value?.getFullValues() || findKpiByReference(activeKpiReference.value);
	if (!kpiValue?.reference) {
		return null;
	}

	const filters = {
		reference: kpiValue.reference,
		accounts: accountSelect.value?.getValues() || [],
		channels: channelSelect.value?.getValues() || [],
		dlts: deliveryLocationTypeSelect.value?.getValues() || [],
		productTrees: productTreeTreeview.value?.getValues() || [],
		specificities: specificitySelect.value?.getValues() || [],
		organisations: organisationTreeview.value?.getValues() || [],
		companies: companySelect.value?.getValues() || [],
	};

	if ('simulated' in mainKpiParams.value) {
		filters.simulated = Boolean(isDetailSimulated.value);
	}

	filters.allPark = allPark.value;

	return filters;
}

async function loadKpiDetails(filters, options = {}) {
	const loadKey = getKpiDetailLoadKey(filters);
	if (loadKey && loadKey === latestKpiDetailLoadKey && !kpiDetailError.value && !isKpiDetailLoading.value && options?.forceReload !== true) {
		return;
	}

	const requestId = ++kpiDetailLoadRequestId;
	const previousGridState = options?.preserveGridState === false ? null : getDetailGridViewState();

	if (!filters?.reference) {
		kpiDetailRows.value = [];
		kpiDetailError.value = null;
		displayedDeliveryLocationCount.value = 0;
		isDetailSimulated.value = false;
		return;
	}

	try {
		isKpiDetailLoading.value = true;
		await nextTick();
		await new Promise((resolve) => requestAnimationFrame(() => requestAnimationFrame(resolve)));
		await new Promise((resolve) => requestAnimationFrame(() => requestAnimationFrame(resolve)));
		await new Promise((resolve) => requestAnimationFrame(() => requestAnimationFrame(resolve)));
		kpiDetailError.value = null;
		activeKpiReference.value = filters.reference;
		currentAppliedFilters.value = { ...filters };
		isDetailSimulated.value = Boolean(filters.simulated);
		const rows = await kpiService.getKpiDetailRows(filters.reference, filters);
		const normalizedRows = (Array.isArray(rows) ? rows : []).map((row) => ({
			...row,
			totalReferencesCount: Number(row?.totalReferencesCount ?? row?.referenceCount ?? 0),
		}));
		const { rows: enrichedRows, columns } = await enrichRowsWithDeliveryLocationAttributes(normalizedRows);
		if (requestId !== kpiDetailLoadRequestId) {
			return;
		}

		kpiDetailRows.value = enrichedRows;
		deliveryLocationAttributeColumns.value = columns;
		latestKpiDetailLoadKey = loadKey;
	} catch (err) {
		if (requestId !== kpiDetailLoadRequestId) {
			return;
		}

		kpiDetailRows.value = [];
		deliveryLocationAttributeColumns.value = [];
		displayedDeliveryLocationCount.value = 0;
		kpiDetailError.value = err.message;
	} finally {
		if (requestId !== kpiDetailLoadRequestId) {
			return;
		}

		isKpiDetailLoading.value = false;
		if (previousGridState && options?.preserveGridState !== false) {
			await nextTick();
			applyDetailGridViewState(previousGridState, { preserveRowGroups: true });
			applyDetailRowGrouping();
		}
	}
}

function getKpiDetailLoadKey(filters) {
	if (!filters?.reference) {
		return '';
	}

	const normalizeValue = (value) => {
		if (Array.isArray(value)) {
			return [...value].sort((left, right) => String(left).localeCompare(String(right)));
		}

		if (value && typeof value === 'object') {
			return Object.keys(value)
				.sort((left, right) => left.localeCompare(right))
				.reduce((normalized, key) => {
					normalized[key] = normalizeValue(value[key]);
					return normalized;
				}, {});
		}

		return value;
	};

	return JSON.stringify(normalizeValue(filters));
}

function applyDetailRowGrouping() {
	if (!kpiDetailGrid.value || typeof kpiDetailGrid.value.setRowGroupColumns !== 'function') {
		return;
	}

	const api = typeof kpiDetailGrid.value.getGridApi === 'function' ? kpiDetailGrid.value.getGridApi() : null;
	const currentGroups =
		typeof api?.getRowGroupColumns === 'function'
			? api
					.getRowGroupColumns()
					.map((column) => column?.getColId?.())
					.filter(Boolean)
			: [];
	if (haveSameItems(currentGroups, selectedGridRowGroups.value)) {
		return;
	}

	kpiDetailGrid.value.setRowGroupColumns(selectedGridRowGroups.value);
}

function bumpViewStateVersion() {
	viewStateVersion.value += 1;
}

function onKpiDetailGridReady() {
	isKpiDetailGridReady.value = true;
	while (detailGridReadyResolvers.length > 0) {
		detailGridReadyResolvers.shift()?.();
	}
}

function onDetailGridViewStateChanged() {
	bumpViewStateVersion();
}

function getDetailGridViewState() {
	return typeof kpiDetailGrid.value?.getViewState === 'function' ? kpiDetailGrid.value.getViewState() : null;
}

function applyDetailGridViewState(gridState, options = {}) {
	if (!gridState || typeof kpiDetailGrid.value?.applyViewState !== 'function') {
		return;
	}

	kpiDetailGrid.value.applyViewState(gridState, options);
}

async function waitForDetailGridReady(timeoutMs = 2000) {
	if (isKpiDetailGridReady.value && typeof kpiDetailGrid.value?.getGridApi === 'function' && kpiDetailGrid.value.getGridApi()) {
		return true;
	}

	return await new Promise((resolve) => {
		const timeoutId = setTimeout(() => {
			const resolverIndex = detailGridReadyResolvers.indexOf(onReady);
			if (resolverIndex >= 0) {
				detailGridReadyResolvers.splice(resolverIndex, 1);
			}
			resolve(false);
		}, timeoutMs);

		const onReady = () => {
			clearTimeout(timeoutId);
			resolve(true);
		};

		detailGridReadyResolvers.push(onReady);
	});
}

function normalizeRowGroupColumns(colIds = []) {
	const availableColumns = new Set(quickRowGroupDefinitions.value.flatMap((group) => (Array.isArray(group.levels) ? group.levels.map((level) => level.columnField) : [group.columnField])).filter((columnField) => typeof columnField === 'string' && columnField.length > 0));

	return [...new Set((Array.isArray(colIds) ? colIds : []).filter((colId) => typeof colId === 'string' && availableColumns.has(colId)))];
}

function getUserViewState() {
	const filters = buildFiltersFromControls() || buildFiltersFromParams(mainKpiParams.value);
	if (!filters?.reference) {
		return null;
	}

	const gridState = getDetailGridViewState();

	return {
		filters: getUserViewFilters(filters),
		quickGroups: [...selectedGridRowGroups.value],
		showAdditionalFilters: showAdditionalFilters.value,
		allPark: allPark.value,
		showKpiDetailGrid: showKpiDetailGrid.value,
		gridState,
	};
}

function getDefaultUserViewState() {
	const filters = buildFiltersFromControls() || buildFiltersFromParams(mainKpiParams.value);
	if (!filters?.reference) {
		return null;
	}

	return {
		filters: getUserViewFilters(filters),
		quickGroups: [...DEFAULT_DETAIL_GRID_ROW_GROUPS],
		showAdditionalFilters: false,
		showKpiDetailGrid: false,
		gridState: buildDefaultDetailGridState(),
	};
}

async function applyUserViewState(viewState = {}) {
	const targetReference = getCurrentKpiReference(viewState?.filters?.reference);
	const filters = buildFiltersFromParams({
		...(viewState?.filters || {}),
		reference: targetReference,
	});
	if (!filters?.reference) {
		throw new Error('Invalid user view state');
	}

	isApplyingViewState = true;
	try {
		error.value = null;
		hasAppliedFilters.value = true;
		activeKpiReference.value = filters.reference;
		allPark.value = Boolean(viewState?.allPark ?? true);
		showKpiDetailGrid.value = Boolean(viewState?.showKpiDetailGrid ?? false);

		await syncFiltersFromParams(filters);
		selectedGridRowGroups.value = normalizeRowGroupColumns(Array.isArray(viewState?.quickGroups) ? viewState.quickGroups : DEFAULT_DETAIL_GRID_ROW_GROUPS);

		if (showKpiDetailGrid.value) {
			await loadKpiDetails(filters, { preserveGridState: false });
			await nextTick();
			await waitForDetailGridReady();

			applyDetailRowGrouping();
			await nextTick();
			applyDetailGridViewState(viewState?.gridState, { preserveRowGroups: true });
		}

		updateSelectedFilterChips();
		bumpViewStateVersion();
	} finally {
		isApplyingViewState = false;
	}
}

function buildDefaultDetailGridState() {
	return {
		columnState: buildDefaultDetailGridColumnState(),
		filterModel: {},
		rowGroupColumns: [...DEFAULT_DETAIL_GRID_ROW_GROUPS],
	};
}

function buildDefaultDetailGridColumnState() {
	const visibleColumnIds = new Set(DEFAULT_DETAIL_GRID_VISIBLE_COLUMNS);
	const groupColumnIds = new Set(DEFAULT_DETAIL_GRID_ROW_GROUPS);

	return getLeafColumnIds(kpiDetailColumns.value).map((colId) => ({
		colId,
		hide: !visibleColumnIds.has(colId),
		rowGroup: groupColumnIds.has(colId),
		rowGroupIndex: DEFAULT_DETAIL_GRID_ROW_GROUPS.indexOf(colId) >= 0 ? DEFAULT_DETAIL_GRID_ROW_GROUPS.indexOf(colId) : null,
	}));
}

function getLeafColumnIds(columnDefs = []) {
	return (Array.isArray(columnDefs) ? columnDefs : []).flatMap((columnDef) => {
		if (!columnDef || typeof columnDef !== 'object') {
			return [];
		}

		if (Array.isArray(columnDef.children) && columnDef.children.length > 0) {
			return getLeafColumnIds(columnDef.children);
		}

		const colId = columnDef.colId || columnDef.field;
		return typeof colId === 'string' && colId.length > 0 ? [colId] : [];
	});
}

function onUserViewApplied(view) {
	const isDefaultView = view?.id === USER_VIEW_DEFAULT_ID;
	defaultActiveGridRowGroups.value = isDefaultView ? [...DEFAULT_DETAIL_GRID_ROW_GROUPS] : [];
}

function getCurrentKpiReference(fallbackReference = null) {
	return activeKpiReference.value || props.params?.reference || kpiReferenceSelect.value?.getFullValues()?.reference || fallbackReference || '';
}

function getUserViewFilters(filters = {}) {
	if (!filters || typeof filters !== 'object') {
		return {};
	}

	const { reference, ...userViewFilters } = filters;
	void reference;
	return userViewFilters;
}

function onDisplayedRowCountChanged(rowCount) {
	void rowCount;
	displayedDeliveryLocationCount.value = getDisplayedUniqueDeliveryLocationCount();
}

function getDisplayedUniqueDeliveryLocationCount() {
	const gridApi = typeof kpiDetailGrid.value?.getGridApi === 'function' ? kpiDetailGrid.value.getGridApi() : null;
	if (!gridApi || typeof gridApi.forEachNodeAfterFilter !== 'function') {
		return [...new Set((Array.isArray(filteredKpiDetailRows.value) ? filteredKpiDetailRows.value : []).map((row) => getDeliveryLocationIdFromRow(row)).filter(Boolean))].length;
	}

	const deliveryLocationIds = new Set();
	gridApi.forEachNodeAfterFilter((node) => {
		if (node?.group || !node?.data) {
			return;
		}

		const deliveryLocationId = getDeliveryLocationIdFromRow(node.data);
		if (deliveryLocationId) {
			deliveryLocationIds.add(deliveryLocationId);
		}
	});

	return deliveryLocationIds.size;
}

function onGridRowGroupColumnsChanged(colIds = []) {
	const normalizedColIds = [...new Set((Array.isArray(colIds) ? colIds : []).filter((colId) => typeof colId === 'string' && colId.length > 0))];
	if (haveSameItems(normalizedColIds, selectedGridRowGroups.value)) {
		return;
	}

	selectedGridRowGroups.value = normalizedColIds;
}

function haveSameItems(first = [], second = []) {
	if (first.length !== second.length) {
		return false;
	}

	const secondSet = new Set(second);
	return first.every((item) => secondSet.has(item));
}

function getDeliveryLocationIdFromRow(row) {
	const id = row?.deliveryLocationId ?? row?.id;
	const numericId = Number(id);
	return Number.isInteger(numericId) && numericId > 0 ? numericId : null;
}

function getDeliveryLocationIdFromAttributeItem(item) {
	const id = item?.deliveryLocationId ?? item?.id;
	const numericId = Number(id);
	return Number.isInteger(numericId) && numericId > 0 ? numericId : null;
}

function getAttributeId(attribute) {
	const id = attribute?.attributeId ?? attribute?.id;
	const numericId = Number(id);
	return Number.isInteger(numericId) && numericId > 0 ? numericId : null;
}

function getAttributeField(attributeId) {
	return `deliveryLocationAttribute_${attributeId}`;
}

function getAttributeColumnHeader(attributeId) {
	const attributeDefinition = attributesById.value.get(attributeId);
	return attributeDefinition?.name || `${t('Attribute')} ${attributeId}`;
}

function buildAttributeColumn(attributeId) {
	return {
		field: getAttributeField(attributeId),
		headerName: getAttributeColumnHeader(attributeId),
		sortable: true,
		filter: true,
		resizable: true,
		minWidth: 160,
		hide: true,
		columnGroupShow: 'open',
	};
}

function isDefaultDetailGridColumnHidden(colId) {
	return !DEFAULT_DETAIL_GRID_VISIBLE_COLUMNS.includes(colId);
}

function getNormalizedAttributeValue(attribute) {
	if (attribute?.value === null || attribute?.value === undefined) {
		return '';
	}

	if (typeof attribute.value === 'object') {
		return JSON.stringify(attribute.value);
	}

	return String(attribute.value);
}

async function enrichRowsWithDeliveryLocationAttributes(rows = []) {
	if (!Array.isArray(rows) || rows.length === 0) {
		return { rows: [], columns: [] };
	}

	const deliveryLocationIds = [...new Set(rows.map((row) => getDeliveryLocationIdFromRow(row)).filter(Boolean))];
	if (!deliveryLocationIds.length) {
		return { rows, columns: [] };
	}

	const attributeItems = await getCachedDeliveryLocationAttributes(deliveryLocationIds);

	const rowsByDeliveryLocationId = new Map();
	const enrichedRows = rows.map((row) => {
		const rowCopy = { ...row };
		const deliveryLocationId = getDeliveryLocationIdFromRow(rowCopy);
		if (deliveryLocationId) {
			rowsByDeliveryLocationId.set(deliveryLocationId, rowCopy);
		}
		return rowCopy;
	});

	const columnsByAttributeId = new Map();

	attributeItems.forEach((item) => {
		const deliveryLocationId = getDeliveryLocationIdFromAttributeItem(item);
		if (!deliveryLocationId) {
			return;
		}

		const targetRow = rowsByDeliveryLocationId.get(deliveryLocationId);
		if (!targetRow) {
			return;
		}

		const attributes = Array.isArray(item?.attributes) ? item.attributes : [];
		attributes.forEach((attribute) => {
			const attributeId = getAttributeId(attribute);
			if (!attributeId) {
				return;
			}

			if (!columnsByAttributeId.has(attributeId)) {
				columnsByAttributeId.set(attributeId, buildAttributeColumn(attributeId));
			}

			targetRow[getAttributeField(attributeId)] = getNormalizedAttributeValue(attribute);
		});
	});

	const columns = [...columnsByAttributeId.entries()].sort(([firstId], [secondId]) => firstId - secondId).map(([, column]) => column);
	return { rows: enrichedRows, columns };
}

async function getCachedDeliveryLocationAttributes(deliveryLocationIds = []) {
	const cacheKey = [...deliveryLocationIds].sort((left, right) => left - right).join('|');
	if (deliveryLocationAttributeCache.has(cacheKey)) {
		return deliveryLocationAttributeCache.get(cacheKey);
	}

	try {
		const response = await getDeliveryLocationAttributes(deliveryLocationIds);
		const attributeItems = Array.isArray(response) ? response : [];
		deliveryLocationAttributeCache.set(cacheKey, attributeItems);
		return attributeItems;
	} catch {
		return [];
	}
}

async function resetFilters() {
	Object.values(filterRefs).forEach((filterRef) => {
		if (filterRef.value && typeof filterRef.value.isRequired === 'function' && filterRef.value.isRequired()) return;
		if (filterRef.value && typeof filterRef.value.reset === 'function') {
			filterRef.value.reset();
		}
	});

	allPark.value = false;
	selectedFilterChips.value = [];
	kpiDetailSearch.value = '';
	hasAppliedFilters.value = true;
	if (showKpiDetailGrid.value) {
		await loadKpiDetails(buildFiltersFromControls());
	}
}

async function applyFilters(options = {}) {
	const skipPersistGeneratedCustomKpi = options?.skipPersistGeneratedCustomKpi === true;
	const selectedKpi = kpiReferenceSelect.value?.getFullValues();
	if (!selectedKpi) {
		error.value = t('PleaseSelectKpi');
		return;
	}

	error.value = null;
	hasAppliedFilters.value = true;
	activeKpiReference.value = selectedKpi.reference;

	const filters = buildFiltersFromControls(selectedKpi);
	currentAppliedFilters.value = { ...filters };
	isDetailSimulated.value = Boolean(filters?.simulated);
	selectedFilterChips.value = getSelectedFilterChips({
		organisations: organisationTreeview.value?.getFullValues() || [],
		deliveryLocationTypes: deliveryLocationTypeSelect.value?.getFullValues() || [],
		accounts: accountSelect.value?.getFullValues() || [],
		channels: channelSelect.value?.getFullValues() || [],
		productTrees: productTreeTreeview.value?.getFullValues() || [],
		specificities: specificitySelect.value?.getFullValues() || [],
		companies: companySelect.value?.getFullValues() || [],
	});

	if (showKpiDetailGrid.value) {
		await loadKpiDetails(filters);
	}

	if (!skipPersistGeneratedCustomKpi) {
		await persistGeneratedCustomKpi(filters, selectedKpi);
	}

	bumpViewStateVersion();
}

async function showDetailGrid() {
	showKpiDetailGrid.value = true;
	const filters = buildFiltersFromControls() || buildFiltersFromParams(currentAppliedFilters.value || props.params);
	if (!filters?.reference) {
		return;
	}
	await loadKpiDetails(filters);
	await nextTick();
	await waitForDetailGridReady();
	applyDetailRowGrouping();
}

async function reapplyKpiFilters(params = {}) {
	const filters = buildFiltersFromParams(params);
	if (!filters?.reference) {
		return;
	}

	filters.allPark = allPark.value;

	error.value = null;
	activeKpiReference.value = filters.reference;
	showKpiDetailGrid.value = true;
	await syncFiltersFromParams(filters);
	await loadKpiDetails(filters);
}

async function onKpiSimulatedChanged({ guid, reference, isSimulated }) {
	if (!reference || !hasSameReference(reference, activeKpiReference.value)) {
		return;
	}

	let sourceParams = mainKpiParams.value;
	if (guid) {
		const matchingCustomKpi = kpiComponents.value.find((customKpi) => customKpi.guid === guid);
		sourceParams = matchingCustomKpi?.params || sourceParams;
	}

	const filters = buildFiltersFromParams({
		...(sourceParams || {}),
		reference,
		simulated: !!isSimulated,
	});

	if (!filters?.reference) {
		return;
	}

	error.value = null;
	activeKpiReference.value = filters.reference;
	isDetailSimulated.value = Boolean(filters.simulated);
	if (showKpiDetailGrid.value) {
		await loadKpiDetails(filters);
	}
	bumpViewStateVersion();
}

function getKpiDetailRowId(params) {
	const row = params?.data || {};
	return String(row.id ?? `${row.deliveryLocationId ?? 'dl'}-${row.productTreeId ?? row.reference ?? `${row.companyName || ''}-${row.city || ''}`}`);
}

function updateSelectedFilterChips() {
	selectedFilterChips.value = getSelectedFilterChips({
		organisations: organisationTreeview.value?.getFullValues() || [],
		deliveryLocationTypes: deliveryLocationTypeSelect.value?.getFullValues() || [],
		accounts: accountSelect.value?.getFullValues() || [],
		channels: channelSelect.value?.getFullValues() || [],
		productTrees: productTreeTreeview.value?.getFullValues() || [],
		specificities: specificitySelect.value?.getFullValues() || [],
		companies: companySelect.value?.getFullValues() || [],
	});
}

async function onCustomKpiRemoved({ guid }) {
	if (!guid) {
		return;
	}

	localKpiComponents.value = localKpiComponents.value.filter((customKpi) => customKpi.guid !== guid);
	await savePersistedCustomKpis();
}

function hasSameReference(firstReference, secondReference) {
	if (!firstReference || !secondReference) {
		return false;
	}

	return firstReference.toLowerCase() === secondReference.toLowerCase();
}

function findKpiByReference(reference) {
	if (!reference) {
		return null;
	}

	return kpis.value.find((kpiItem) => hasSameReference(kpiItem.reference, reference)) || null;
}

function getKpiTitle(reference) {
	return findKpiByReference(reference)?.reference || null;
}

function mapWidgetToCustomKpi(widget) {
	return {
		title: getKpiTitle(widget.params?.reference) || widget.params?.reference || widget.title,
		color: props.color,
		width: props.width,
		params: widget.params,
		annotation: widget.title,
		custom: true,
		guid: widget.guid || widget.id,
	};
}

function mapStoredCustomKpi(customKpi) {
	const params = buildFiltersFromParams(customKpi?.params || customKpi);
	if (!params?.reference) {
		return null;
	}

	return {
		title: customKpi.title || getKpiTitle(params.reference) || params.reference,
		color: customKpi.color || 'primary',
		width: Number.isFinite(Number(customKpi.width)) ? Number(customKpi.width) : 12,
		params,
		annotation: customKpi.annotation || getAnnotationForFilters(params),
		custom: true,
		guid: customKpi.guid || crypto.randomUUID(),
	};
}

function mapCustomKpiToSetting(customKpi) {
	const params = buildFiltersFromParams(customKpi?.params);
	if (!params?.reference) {
		return null;
	}

	return {
		guid: customKpi.guid || crypto.randomUUID(),
		title: customKpi.title || getKpiTitle(params.reference) || params.reference,
		color: customKpi.color || 'primary',
		width: Number.isFinite(Number(customKpi.width)) ? Number(customKpi.width) : 12,
		annotation: customKpi.annotation || getAnnotationForFilters(params),
		params,
	};
}

function buildGeneratedCustomKpi(filters, selectedKpi) {
	if (!filters?.reference) {
		return null;
	}

	return {
		title: getKpiTitle(selectedKpi?.reference || filters.reference) || selectedKpi?.reference || filters.reference,
		color: 'primary',
		width: 12,
		params: filters,
		annotation: getAnnotationForFilters(filters),
		custom: true,
		guid: crypto.randomUUID(),
	};
}

function customKpiAlreadyExists(filters) {
	return localKpiComponents.value.some((customKpi) => {
		if (!hasSameReference(customKpi.params?.reference, filters.reference)) {
			return false;
		}

		const customFilters = {
			organisations: (customKpi.params?.organisations || []).sort().join('|'),
			dlts: (customKpi.params?.dlts || customKpi.params?.deliveryLocationTypes || []).sort().join('|'),
			accounts: (customKpi.params?.accounts || []).sort().join('|'),
			channels: (customKpi.params?.channels || []).sort().join('|'),
			productTrees: (customKpi.params?.productTrees || customKpi.params?.pts || []).sort().join('|'),
			specificities: (customKpi.params?.specificities || customKpi.params?.spes || []).sort().join('|'),
			companies: (customKpi.params?.companies || []).sort().join('|'),
			simulated: customKpi.params?.simulated,
		};

		const currentFilters = {
			organisations: (filters.organisations || []).sort().join('|'),
			dlts: (filters.dlts || filters.deliveryLocationTypes || []).sort().join('|'),
			accounts: (filters.accounts || []).sort().join('|'),
			channels: (filters.channels || []).sort().join('|'),
			productTrees: (filters.productTrees || filters.pts || []).sort().join('|'),
			specificities: (filters.specificities || filters.spes || []).sort().join('|'),
			companies: (filters.companies || []).sort().join('|'),
			simulated: filters.simulated,
		};

		return JSON.stringify(customFilters) === JSON.stringify(currentFilters);
	});
}

async function persistGeneratedCustomKpi(filters, selectedKpi) {
	const generatedCustomKpi = buildGeneratedCustomKpi(filters, selectedKpi);
	if (!generatedCustomKpi) {
		return;
	}

	if (customKpiAlreadyExists(filters)) {
		return;
	}

	const hasExistingCustomKpi = localKpiComponents.value.some((customKpi) => hasSameReference(customKpi.params?.reference, filters.reference));

	if (hasExistingCustomKpi) {
		return;
	}

	localKpiComponents.value.push(generatedCustomKpi);
	await savePersistedCustomKpis();
}

async function createCustomKpiManually() {
	const filters = buildFiltersFromControls();
	if (!filters?.reference) {
		error.value = t('PleaseSelectKpi');
		return;
	}

	const selectedKpi = kpiReferenceSelect.value?.getFullValues();
	const customKpi = buildGeneratedCustomKpi(filters, selectedKpi);
	if (!customKpi) {
		return;
	}

	localKpiComponents.value.push(customKpi);
	await savePersistedCustomKpis();
}

async function savePersistedCustomKpis() {
	const persistedCustomKpis = localKpiComponents.value.map((customKpi) => mapCustomKpiToSetting(customKpi)).filter(Boolean);
	userSettings.value = userSettings.value.filter((setting) => setting.name !== KPI_BUILDER_CUSTOMS_SETTING_NAME);
	userSettings.value.push({
		name: KPI_BUILDER_CUSTOMS_SETTING_NAME,
		groupName: 'WEBAPP',
		value: JSON.stringify(persistedCustomKpis),
	});

	const isSaved = await postUserSettings(userSettings.value, { groupName: 'WEBAPP', userId: user.id });
	if (!isSaved) {
		error.value = "Impossible d'enregistrer les KPIs personnalisés.";
	}
}

function formatKpiMetric(value, signed = false) {
	if (value === null || value === undefined || Number.isNaN(Number(value))) {
		return '';
	}

	const numericValue = Number(value);
	const roundedValue = Math.round(numericValue * 100) / 100;
	const displayValue = Number.isInteger(roundedValue) ? `${roundedValue}` : roundedValue.toFixed(2);
	const prefix = signed && roundedValue > 0 ? '+' : '';
	const suffix = isDetailPercentage.value ? '%' : '';
	return `${prefix}${displayValue}${suffix}`;
}

function formatReferenceCount(value) {
	const numericValue = Number(value);
	return Number.isFinite(numericValue) ? `${Math.round(numericValue)}` : '';
}

function getDifferenceCellStyle(value) {
	if (value > 0) {
		return { color: 'rgb(var(--v-theme-success))', fontWeight: 600 };
	}

	if (value < 0) {
		return { color: 'rgb(var(--v-theme-error))', fontWeight: 600 };
	}

	return {};
}

function getKpiDetailGroupRowAgg(params) {
	const leafRows = collectLeafRows(params?.nodes || []);
	const categoryFields = Array.from({ length: productTreeMaxLevel.value || 0 }, (_, index) => `productTreeLevel${index + 1}`);
	const dynamicAttributeFields = deliveryLocationAttributeColumns.value.map((column) => column.field);
	if (!leafRows.length) {
		const emptyCategoryCounts = categoryFields.reduce((counts, field) => {
			counts[field] = 0;
			return counts;
		}, {});
		const emptyDynamicCounts = dynamicAttributeFields.reduce((counts, field) => {
			counts[field] = 0;
			return counts;
		}, {});

		return {
			companyName: 0,
			deliveryLocation: 0,
			parentAccount: 0,
			account: 0,
			channel: 0,
			deliveryLocationType: 0,
			productTree: 0,
			specificities: 0,
			address: 0,
			postcode: 0,
			city: 0,
			organisations: 0,
			value: 0,
			objective: 0,
			difference: 0,
			referenceCount: 0,
			totalReferencesCount: 0,
			classColor: '',
			color: 'primary',
			...emptyCategoryCounts,
			...emptyDynamicCounts,
		};
	}

	const activeRows = leafRows.filter((row) => row?.isActive);
	const aggregationMode = (activeRows[0] || leafRows[0])?.aggregationMode || 'sum';
	const aggregatedCounts = buildCountAggregates(leafRows, ['companyName', 'deliveryLocation', 'parentAccount', 'account', 'channel', 'deliveryLocationType', 'productTree', 'specificities', 'address', 'postcode', 'city', 'posType', 'cluster', 'locationGroup', 'distributionChannel', 'seasonality', 'organisations', ...categoryFields, ...dynamicAttributeFields]);

	if (aggregationMode === 'ratio') {
		let ratioValue;
		let totalReferencesCountForGroup;

		if (currentAppliedFilters.value?.allPark && !currentAppliedFilters.value?.simulated) {
			// allPark real mode: numerator from visited rows, denominator from ALL rows
			// (unvisited rows have totalReferencesCount = their assortment size, computed in _buildKpiDetailRows)
			const numerator = getSumValue(activeRows.map((row) => row.referenceCount));
			const denominator = getSumValue(leafRows.map((row) => row.totalReferencesCount));
			ratioValue = denominator > 0 ? roundMetricValue((numerator / denominator) * 100) : 0;
			totalReferencesCountForGroup = getCountSum(leafRows.map((row) => row.totalReferencesCount));
		} else {
			ratioValue = getGroupedRatioValue(activeRows);
			totalReferencesCountForGroup = getCountSum(activeRows.map((row) => row.totalReferencesCount));
		}

		const rowsWithRealObjective = activeRows.filter((row) => row._hasRealObjective);
		const objective = rowsWithRealObjective.length > 0 ? roundMetricValue(getAverageValue(rowsWithRealObjective.map((row) => row.objective))) : 0;
		const { classColor, color } = getKpiColors(ratioValue, objective);
		return {
			...aggregatedCounts,
			value: ratioValue,
			objective,
			difference: roundMetricValue(ratioValue - objective),
			referenceCount: getCountSum(activeRows.map((row) => row.referenceCount)),
			totalReferencesCount: totalReferencesCountForGroup,
			classColor,
			color,
		};
	}

	const value = roundMetricValue(getSumValue(activeRows.map((row) => row.rawValue ?? row.value)));
	const rowsWithRealObjectiveForSum = activeRows.filter((row) => row._hasRealObjective);
	const objective = rowsWithRealObjectiveForSum.length > 0 ? roundMetricValue(getSumValue(rowsWithRealObjectiveForSum.map((row) => row.objective))) : 0;
	const { classColor, color } = getKpiColors(value, objective);
	return {
		...aggregatedCounts,
		value,
		objective,
		difference: roundMetricValue(value - objective),
		referenceCount: getCountSum(activeRows.map((row) => row.referenceCount)),
		totalReferencesCount: getCountSum(activeRows.map((row) => row.totalReferencesCount)),
		classColor,
		color,
	};
}

function getKpiColors(value, objective) {
	const reference = activeKpiReference.value || props.params.reference || '';
	if (!reference || typeof kpiService.defineColors !== 'function') {
		return { classColor: '', color: 'primary' };
	}

	const kpiDefinition = findKpiByReference(reference);
	const tolerance = Number(kpiDefinition?.tolerance ?? 0);
	const isInverted = typeof kpiService._isInvertedKpi === 'function' ? kpiService._isInvertedKpi(reference) : false;
	return kpiService.defineColors(value, objective, isInverted, tolerance);
}

function collectLeafRows(nodes = []) {
	const rows = [];
	const seenRowIds = new Set();

	const visitNode = (node) => {
		if (!node) {
			return;
		}

		const children = node.childrenAfterFilter || node.childrenAfterGroup || node.childrenAfterSort;
		if (Array.isArray(children) && children.length > 0) {
			children.forEach(visitNode);
			return;
		}

		if (!node.data) {
			return;
		}

		// Dedup by deliveryLocationId + productTreeId so stores shared across multiple
		// organisations (cloned via _organisationPaths into rows with `__orgN` suffixed ids)
		// are counted only once at parent group levels.
		const rowKey = `${node.data.deliveryLocationId ?? 'dl'}-${node.data.productTreeId ?? 'pt'}`;
		if (seenRowIds.has(rowKey)) {
			return;
		}

		seenRowIds.add(rowKey);
		rows.push(node.data);
	};

	nodes.forEach(visitNode);
	return rows;
}

function getGroupedRatioValue(rows = []) {
	const numerator = getSumValue(rows.map((row) => row.referenceCount));
	const denominator = getSumValue(rows.map((row) => row.totalReferencesCount));

	if (!denominator) {
		return 0;
	}

	return roundMetricValue((numerator / denominator) * 100);
}

function buildCountAggregates(rows = [], fields = []) {
	return fields.reduce((aggregates, field) => {
		aggregates[field] = countNonEmptyValues(rows, field);
		return aggregates;
	}, {});
}

function countNonEmptyValues(rows = [], field) {
	return rows.reduce((count, row) => {
		const value = row?.[field];
		if (value === null || value === undefined || value === '') {
			return count;
		}

		return count + 1;
	}, 0);
}

function getAverageValue(values = []) {
	const numericValues = values.map((value) => Number(value)).filter((value) => Number.isFinite(value));

	if (!numericValues.length) {
		return 0;
	}

	return getSumValue(numericValues) / numericValues.length;
}

function getSumValue(values = []) {
	return values.reduce((sum, value) => {
		const numericValue = Number(value);
		return Number.isFinite(numericValue) ? sum + numericValue : sum;
	}, 0);
}

function getCountSum(values = []) {
	return Math.round(getSumValue(values));
}

function roundMetricValue(value) {
	const numericValue = Number(value);
	return Number.isFinite(numericValue) ? Number(numericValue.toFixed(2)) : 0;
}

function getAnnotationForFilters(filters) {
	const parts = [];

	if (filters.organisations && filters.organisations.length > 0) {
		if (filters.organisations.length === 1) {
			const org = organisationTreeview.value?.getFullValues()?.[0];
			parts.push(`${org ? org.label : filters.organisations[0]}`);
		} else {
			parts.push(`${t('Organisations')}: ${filters.organisations.length}`);
		}
	}

	if (filters.dlts && filters.dlts.length > 0) {
		if (filters.dlts.length === 1) {
			const deliveryLocationType = deliveryLocationTypeSelect.value?.getFullValues()?.[0];
			parts.push(`${deliveryLocationType ? deliveryLocationType.name : filters.dlts[0]}`);
		} else {
			parts.push(`${t('DeliveryLocationType')}: ${filters.dlts.length}`);
		}
	}

	if (filters.accounts && filters.accounts.length > 0) {
		if (filters.accounts.length === 1) {
			const account = accountSelect.value?.getFullValues()?.[0];
			parts.push(`${account ? account.name : filters.accounts[0]}`);
		} else {
			parts.push(`${t('Accounts')}: ${filters.accounts.length}`);
		}
	}

	if (filters.channels && filters.channels.length > 0) {
		if (filters.channels.length === 1) {
			const channel = channelSelect.value?.getFullValues()?.[0];
			parts.push(`${channel ? channel.name : filters.channels[0]}`);
		} else {
			parts.push(`${t('Channels')}: ${filters.channels.length}`);
		}
	}

	if (filters.productTrees && filters.productTrees.length > 0) {
		if (filters.productTrees.length === 1) {
			const productTree = productTreeTreeview.value?.getFullValues()?.[0];
			parts.push(`${productTree ? productTree.label : filters.productTrees[0]}`);
		} else {
			parts.push(`${t('ProductTrees')}: ${filters.productTrees.length}`);
		}
	}

	if (filters.specificities && filters.specificities.length > 0) {
		if (filters.specificities.length === 1) {
			const specificity = specificitySelect.value?.getFullValues()?.[0];
			parts.push(`${specificity ? specificity.value : filters.specificities[0]}`);
		} else {
			parts.push(`${t('Specificities')}: ${filters.specificities.length}`);
		}
	}

	if (filters.companies && filters.companies.length > 0) {
		if (filters.companies.length === 1) {
			const company = companySelect.value?.getFullValues()?.[0];
			parts.push(`${company ? company.name : filters.companies[0]}`);
		} else {
			parts.push(`${t('Companies')}: ${filters.companies.length}`);
		}
	}

	if (parts.length === 0) {
		return t('KpiCustom');
	}

	return parts.join(' | ');
}

function getSelectedFilterChips(selections) {
	const chips = [];
	addFilterChip(chips, 'organisations', t('Organisations'), selections.organisations, 'label');
	addFilterChip(chips, 'deliveryLocationTypes', t('DeliveryLocationType'), selections.deliveryLocationTypes, 'name');
	addFilterChip(chips, 'accounts', t('Accounts'), selections.accounts, 'name');
	addFilterChip(chips, 'channels', t('Channels'), selections.channels, 'name');
	addFilterChip(chips, 'productTrees', t('ProductTrees'), selections.productTrees, 'label');
	addFilterChip(chips, 'specificities', t('Specificities'), selections.specificities, 'value');
	addFilterChip(chips, 'companies', t('Companies'), selections.companies, 'name');
	return chips;
}

function addFilterChip(target, filterKey, groupLabel, values, valueKey) {
	if (!Array.isArray(values) || values.length === 0) {
		return;
	}

	const displayValues = values
		.map((value) => value?.[valueKey] ?? value?.name ?? value?.label ?? value?.value ?? value?.id)
		.filter((value) => value !== null && value !== undefined && value !== '')
		.map((value) => String(value));

	if (!displayValues.length) {
		return;
	}

	target.push({
		key: `${filterKey}`,
		label: displayValues.length > 2 ? `${groupLabel}: ${displayValues.length}` : `${groupLabel}: ${displayValues.join(', ')}`,
	});
}

async function removeFilterChip(filterKey) {
	const filterMap = {
		organisations: organisationTreeview,
		deliveryLocationTypes: deliveryLocationTypeSelect,
		accounts: accountSelect,
		channels: channelSelect,
		productTrees: productTreeTreeview,
		specificities: specificitySelect,
		companies: companySelect,
	};
	const targetFilterRef = filterMap[filterKey]?.value;

	if (targetFilterRef && typeof targetFilterRef.reset === 'function') {
		targetFilterRef.reset();
	}

	selectedFilterChips.value = selectedFilterChips.value.filter((chip) => chip.key !== filterKey);
	await loadKpiDetails(buildFiltersFromControls());
}
</script>
